import { useEffect, useState } from 'react';
import useApi from '../api';

/**
 * Lifecycle stage definitions + badge, shared by the accounts list, the
 * account detail page, and the org settings editor.
 *
 * Definitions are always fetched from GET /accounts/lifecycle-stages — never
 * read off the UserContext organization (its settings are cached in
 * localStorage and go stale).
 */

export interface LifecycleStageDef {
  key: string;
  label: string;
  color: string;
  order: number;
  system: boolean;
}

/** Constrained color tokens (match backend COLOR_TOKENS), light + dark. */
export const STAGE_TONES: Record<string, string> = {
  gray: 'text-muted-foreground bg-muted',
  blue: 'text-blue-700 dark:text-blue-400 bg-blue-500/10',
  green: 'text-green-700 dark:text-green-400 bg-green-500/10',
  amber: 'text-amber-700 dark:text-amber-400 bg-amber-500/10',
  purple: 'text-purple-700 dark:text-purple-400 bg-purple-500/10',
  red: 'text-red-700 dark:text-red-400 bg-red-500/10',
  teal: 'text-teal-700 dark:text-teal-400 bg-teal-500/10',
  orange: 'text-orange-700 dark:text-orange-400 bg-orange-500/10',
  pink: 'text-pink-700 dark:text-pink-400 bg-pink-500/10',
};

/** Swatch classes for the settings editor's color picker. */
export const STAGE_SWATCHES: Record<string, string> = {
  gray: 'bg-gray-400',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  purple: 'bg-purple-500',
  red: 'bg-red-500',
  teal: 'bg-teal-500',
  orange: 'bg-orange-500',
  pink: 'bg-pink-500',
};

export const stageTone = (color?: string) => STAGE_TONES[color || 'gray'] || STAGE_TONES.gray;

export const StageBadge = ({
  stageKey,
  stages,
}: {
  stageKey?: string | null;
  stages: LifecycleStageDef[];
}) => {
  const key = stageKey || 'anonymous';
  const def = stages.find((s) => s.key === key);
  // Unknown keys (stale definitions, race windows) render defensively.
  const label = def?.label || key;
  return (
    <span
      className={`inline-flex items-center rounded-xl px-2 py-0.5 text-xs font-semibold ${stageTone(def?.color)}`}
    >
      {label}
    </span>
  );
};

export function useLifecycleStages() {
  const { get } = useApi();
  const [stages, setStages] = useState<LifecycleStageDef[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    get('/accounts/lifecycle-stages')
      .then((response) => {
        if (!cancelled) setStages(response.data?.stages || []);
      })
      .catch((error) => console.error('Error fetching lifecycle stages:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { stages, loading };
}
